import { logger } from "@/src/lib/logging/logger";
import { getSupabaseClient } from "@/src/lib/supabase";
import { Database } from "@/src/lib/types/database.types";

export type PaginatedQueryOptions<
  T,
  R extends Database["public"]["Tables"][keyof Database["public"]["Tables"]]["Row"],
> = {
  tableName: keyof Database["public"]["Tables"];
  selectClause: string;
  mapRowToModel: (row: R) => T;
  orderBy?: { column: keyof R; ascending: boolean };
  filter?: (query: any) => any;
  cursor?: { column: keyof R; value: R[keyof R] };
};

export type PaginatedResult<T, R extends Database["public"]["Tables"][keyof Database["public"]["Tables"]]["Row"]> = {
  items: T[];
  totalCount: number;
  hasMore: boolean;
  nextCursor?: { column: keyof R; value: R[keyof R] };
};

export async function paginatedQuery<
  T,
  R extends Database["public"]["Tables"][keyof Database["public"]["Tables"]]["Row"],
>(options: PaginatedQueryOptions<T, R>, pageSize: number = 1000): Promise<PaginatedResult<T, R>> {
  if (pageSize <= 0) {
    throw new Error("Page size must be greater than 0");
  }

  if (pageSize > 1000) {
    throw new Error("Page size cannot exceed 1000");
  }

  const orderColumn = options.orderBy?.column ?? ("id" as keyof R);
  const isAscending = options.orderBy?.ascending ?? true;

  const supabaseClient = await getSupabaseClient();

  let query = supabaseClient
    .from(options.tableName)
    .select(options.selectClause)
    .order(orderColumn as string, { ascending: isAscending })
    .limit(pageSize + 1);

  if (options.cursor) {
    const operator = isAscending ? "gt" : "lt";

    query = query[operator](options.cursor.column as string, options.cursor.value);
  }

  if (options.filter) {
    query = options.filter(query);
  }

  const response = await query;

  if (!response) {
    logger.error(`No response received`, { tableName: options.tableName });

    throw new Error(`No response received from ${options.tableName}`);
  }

  const { data, error } = response;

  if (error) {
    logger.error(`Error fetching paginated data`, {
      tableName: options.tableName,
      error,
    });
    throw error;
  }

  if (!data) {
    throw new Error(`No data received from ${options.tableName}`);
  }

  const typedData = data as unknown as R[];
  const items = typedData.map(options.mapRowToModel);
  const hasMore = items.length > pageSize;
  const resultItems = hasMore ? items.slice(0, -1) : items;

  const nextCursor = hasMore
    ? {
        column: orderColumn,
        value: typedData[pageSize - 1][orderColumn],
      }
    : undefined;

  return {
    items: resultItems,
    totalCount: -1,
    hasMore,
    nextCursor,
  };
}

export async function getAllItems<T, R extends Database["public"]["Tables"][keyof Database["public"]["Tables"]]["Row"]>(
  options: PaginatedQueryOptions<T, R>,
  pageSize: number = 1000
): Promise<T[]> {
  let allItems: T[] = [];
  let currentOptions = { ...options };
  let hasMore = true;

  while (hasMore) {
    const result = await paginatedQuery(currentOptions, pageSize);

    allItems = [...allItems, ...result.items];

    hasMore = result.hasMore;

    if (result.nextCursor) {
      currentOptions = {
        ...currentOptions,
        cursor: result.nextCursor,
      };
    }
  }

  return allItems;
}
